import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { PopupStore } from '@gem/popups';
import { getMascotImageSrc } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { OnboardingType } from 'APP/constants/onboarding';
import { CreateSpacePopup } from 'APP/main/PopupManager/Popups/CreateSpacePopup/CreateSpacePopup';
import { useOnboardingManager } from 'APP/main/hooks/onboardingManager/useOnboardingManager';
import { spaceAnalytics } from 'APP/packages/analytics/analytics';

export interface ISpaceDemoPopupSlide {
  readonly title: string;
  readonly description: string;
  readonly image: string;
  readonly buttonTitle?: string;
  onButtonClick?(): void;
}

export interface ISpaceDemoPopupPresenter {
  currentSlideIndex: number;
  timeoutId: number | null;
  slides: ISpaceDemoPopupSlide[];
  slide: ISpaceDemoPopupSlide;
  isShowPrevButton: boolean;
  isShowNextButton: boolean;
  onSelectSlide(slideIndex: number): void;
  stopSlideChange(): void;
  onPrevSlide(): void;
  onNextSlide(): void;
  onSkipOnboarding(): void;
  onCreateSpace(): void;
  onClose(): void;
}

export function useSpaceDemoPopupPresenter(popupInstance: PopupStore): ISpaceDemoPopupPresenter {
  const { t } = useTranslation();
  const onboardingManager = useOnboardingManager(OnboardingType.SpaceDemo);

  const presenter = useLocalStore<ISpaceDemoPopupPresenter>(() => ({
    currentSlideIndex: 0,
    timeoutId: null,

    get slides(): ISpaceDemoPopupSlide[] {
      return [
        {
          title: t('space_first_story_title'),
          description: t('space_first_story_description'),
          image: getMascotImageSrc(PROJECT_CONFIG.imageAssets.space1),
        },
        {
          title: t('space_second_story_title'),
          description: t('space_second_story_description'),
          image: getMascotImageSrc(PROJECT_CONFIG.imageAssets.space4),
        },
        {
          title: t('space_third_story_title'),
          description: t('space_third_story_description'),
          image: getMascotImageSrc(PROJECT_CONFIG.imageAssets.space3),
        },
        {
          title: t('space_fourth_story_title'),
          description: t('space_fourth_story_description'),
          image: getMascotImageSrc(PROJECT_CONFIG.imageAssets.space2),
          buttonTitle: t('space_create_button'),
          onButtonClick: presenter.onCreateSpace,
        },
      ];
    },

    get slide(): ISpaceDemoPopupSlide {
      return presenter.slides[presenter.currentSlideIndex];
    },

    onSelectSlide(slideIndex): void {
      presenter.currentSlideIndex = slideIndex;
    },

    stopSlideChange(): void {
      if (presenter.timeoutId) {
        clearTimeout(presenter.timeoutId);
        presenter.timeoutId = null;
      }
    },

    get isShowPrevButton(): boolean {
      return presenter.currentSlideIndex !== 0;
    },

    onPrevSlide(): void {
      presenter.currentSlideIndex--;
    },

    get isShowNextButton(): boolean {
      return presenter.currentSlideIndex !== presenter.slides.length - 1;
    },

    onNextSlide(): void {
      presenter.currentSlideIndex++;
    },

    onSkipOnboarding(): void {
      presenter.onClose();
      spaceAnalytics.trackSpaceOnboardingSkipped(presenter.currentSlideIndex + 1);
    },

    onCreateSpace(): void {
      presenter.onClose();
      Tasks.app.openPopup(CreateSpacePopup);
      spaceAnalytics.trackSpaceOnboardingCompleted();
    },

    onClose(): void {
      popupInstance.close();
      onboardingManager.handleClose();
    },
  }));

  useEffect(() => {
    if (!presenter.slides.every((slide) => !!slide.image)) {
      presenter.onClose();
    }
  });

  return presenter;
}
